import React, { Suspense, useCallback } from 'react';
import {
  Box,
  CircularProgress,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import StorefrontIcon from '@mui/icons-material/Storefront';
import HubIcon from '@mui/icons-material/Hub';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import Orders from './Orders';

const OrdersManagement = React.lazy(
  () => import('../../e-commerce/components/OrdersManagement')
);
const InterCompanyB2B = React.lazy(
  () => import('../intercompany/InterCompanyB2B')
);

type OrdersTab = 'cari' | 'eticaret' | 'b2b';

const resolveOrdersTab = (value: string | null): OrdersTab => {
  if (value === 'eticaret' || value === 'b2b') return value;
  return 'cari';
};

const AccountingOrdersPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const { userDetails } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = resolveOrdersTab(searchParams.get('tab'));
  const companyId = userDetails?.companyId ? String(userDetails.companyId) : null;

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, value: OrdersTab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === 'cari') {
            next.delete('tab');
          } else {
            next.set('tab', value);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? 'fullWidth' : 'standard'}
          sx={{
            minHeight: 44,
            '& .MuiTab-root': {
              minHeight: 44,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 14,
            },
          }}
        >
          <Tab
            value="cari"
            icon={<ShoppingCartIcon fontSize="small" />}
            iconPosition="start"
            label={tt('Cari Siparişler', 'Account Orders')}
          />
          <Tab
            value="eticaret"
            icon={<StorefrontIcon fontSize="small" />}
            iconPosition="start"
            label={tt('E-Ticaret Siparişleri', 'E-Commerce Orders')}
          />
          <Tab
            value="b2b"
            icon={<HubIcon fontSize="small" />}
            iconPosition="start"
            label={tt('B2B Bağlantıları', 'B2B Connections')}
          />
        </Tabs>
      </Box>

      {activeTab === 'cari' && <Orders />}

      {activeTab === 'eticaret' && (
        <Suspense
          fallback={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 320,
              }}
            >
              <CircularProgress />
            </Box>
          }
        >
          <OrdersManagement companyId={companyId} embedded />
        </Suspense>
      )}

      {activeTab === 'b2b' && (
        <Suspense
          fallback={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 320,
              }}
            >
              <CircularProgress />
            </Box>
          }
        >
          <InterCompanyB2B embedded />
        </Suspense>
      )}
    </Box>
  );
};

export default AccountingOrdersPage;
